import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { useToast } from "../../components/ui/toast/ToastProvider";
import { useFamilyMode } from "../../context/FamilyModeContext";
import { useFamilyPermissions } from "../../context/FamilyPermissionsContext";
import { useActiveFamily } from "../../context/ActiveFamilyContext";
import { checkUserHasFamily } from "../../utils/authCheck";
import GlobalLoader from "../common/GlobalLoader";
import { useTranslation } from "react-i18next";

type Props = {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireCanCreateTasks?: boolean;
  allowSuperAdmin?: boolean;
};

const RequireAuth: React.FC<Props> = ({
  children,
  requireAdmin = false,
  requireCanCreateTasks = false,
  allowSuperAdmin = true,
}) => {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const location = useLocation();
  const { showToast } = useToast();
  const { enabled: familyMode } = useFamilyMode();
  const { permissions, loading: permissionsLoading } = useFamilyPermissions();
  const { activeFamily, availableFamilies, loading: activeFamilyLoading } = useActiveFamily();

  const [userDoc, setUserDoc] = useState<any | null>(null);
  const [hasFamilies, setHasFamilies] = useState<boolean>(false);
  const [onboardingCheckComplete, setOnboardingCheckComplete] =
    useState<boolean>(false);
  const [isFromOnboarding, setIsFromOnboarding] = useState<boolean>(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem('require_auth_initial_load_complete') === '1';
    } catch {
      return false;
    }
  });

  const notifiedRef = useRef<{ admin?: boolean; tasks?: boolean }>({});

  // Detectar si viene de onboarding/registro
  useEffect(() => {
    const fromOnboarding = location.pathname === '/onboarding' ||
                          sessionStorage.getItem('completing_onboarding') === 'true';
    setIsFromOnboarding(fromOnboarding);
  }, [location.pathname]);

  // Allow time for Firebase to restore session on initial load
  useEffect(() => {
    if (initialLoadComplete) return;

    // Si AuthContext ya resolvió la autenticación, no esperar 4 segundos
    if (!loading) {
      setInitialLoadComplete(true);
      try {
        sessionStorage.setItem('require_auth_initial_load_complete', '1');
      } catch {
      }
      return;
    }

    const timer = setTimeout(() => {
      setInitialLoadComplete(true);
      try {
        sessionStorage.setItem('require_auth_initial_load_complete', '1');
      } catch {
      }
    }, 4000); // Límite de seguridad máximo

    return () => clearTimeout(timer);
  }, [initialLoadComplete, loading]);

  // Escuchar cambios en el documento de usuario
  useEffect(() => {
    if (!user?.uid) return;
    const userDocRef = doc(db, "users", user.uid);
    const unsub = onSnapshot(
      userDocRef,
      (snap) => {
        setUserDoc(snap.exists() ? snap.data() : null);
      },
      (error) => {
        setUserDoc(null);
      }
    );

    return () => unsub();
  }, [user?.uid]);

  // Check onboarding
  useEffect(() => {
    if (!user) {
      setOnboardingCheckComplete(true);
      return;
    }
    if (!userDoc && activeFamilyLoading) return;

    const hasFamilyId =
      Boolean(activeFamily?.id) ||
      availableFamilies.length > 0 ||
      checkUserHasFamily(userDoc) ||
      Boolean(localStorage.getItem('activeFamilyId'));

    setHasFamilies(hasFamilyId);
    setOnboardingCheckComplete(true);
  }, [user, userDoc, activeFamily?.id, availableFamilies.length, activeFamilyLoading]);

  const isSuperAdmin = useMemo(
    () => allowSuperAdmin && userDoc?.role === "superadmin",
    [userDoc?.role, allowSuperAdmin]
  );

  // Usar permisos del contexto (basados en el grupo activo)
  const canAdmin = useMemo(
    () => isSuperAdmin || permissions.isAdmin,
    [isSuperAdmin, permissions.isAdmin]
  );

  const canCreateTasks = useMemo(
    () => isSuperAdmin || permissions.canCreateTasks,
    [isSuperAdmin, permissions.canCreateTasks]
  );

  // Member role restrictions: solo pueden acceder a HomeFamiliar, Board y Preferences
  const path = location.pathname;
  const isMemberRole = permissions.role === "member" && !permissions.isAdmin;
  const memberAllowedPaths = [
    "/groups/home-groups",
    "/groups/board",
    "/preferences",
    "/settings/data-management", // Gestión de datos - cualquier usuario puede acceder
  ];
  
  const shouldRedirectMember = user && hasFamilies && isMemberRole && !memberAllowedPaths.some((p) => path.startsWith(p));

  // Family Mode: permitir todas las rutas de grupos
  const familyAllowed = [
    "/groups/",  // ✅ Permite TODAS las rutas que empiecen con /groups/
    "/groups",   // ✅ Para rutas exactas sin barra final
    "/member/",  // ✅ Permite todas las rutas de member
    "/member",   // ✅ Para rutas exactas sin barra final
    "/family/",  // ✅ Permite rutas de family
    "/family",   // ✅ Para rutas exactas sin barra final
    "/rewards/", // ✅ Permite rutas de rewards (marketplace, history, etc.)
    "/rewards",  // ✅ Marketplace principal sin barra
    "/calendar", // ✅ Calendario
    "/preferences", // Preferencias
    "/notifications", // Notificaciones
    "/profile", // Perfil
  ];
  const shouldRedirectFamilyMode = user && hasFamilies && familyMode && !familyAllowed.some((p) => path.startsWith(p));
  // ✅ Solo redirigir si los permisos ya cargaron para evitar redirects prematuros
  const shouldRedirectAdmin = user && hasFamilies && requireAdmin && !permissionsLoading && !canAdmin;
  const shouldRedirectTasks = user && hasFamilies && requireCanCreateTasks && !permissionsLoading && !canCreateTasks;

  // Mostrar toasts en useEffect para evitar setState durante render - SIEMPRE se ejecutan
  useEffect(() => {
    if (shouldRedirectMember && !notifiedRef.current.admin && !notifiedRef.current.tasks) {
      showToast({
        type: "error",
        message: t(
          "auth.memberRestricted",
          "Acceso restringido: los miembros solo pueden acceder a Home, Board y Preferencias"
        ),
      });
      notifiedRef.current.admin = true;
    }
  }, [shouldRedirectMember, showToast, t]);

  useEffect(() => {
    if (shouldRedirectFamilyMode && !notifiedRef.current.admin && !notifiedRef.current.tasks) {
      showToast({
        type: "error",
        message: t(
          "auth.familyModeActive",
          "Modo Familia activo: solo las vistas familiares están disponibles"
        ),
      });
      notifiedRef.current.admin = true;
    }
  }, [shouldRedirectFamilyMode, showToast, t]);

  useEffect(() => {
    if (shouldRedirectAdmin && !notifiedRef.current.admin) {
      notifiedRef.current.admin = true;
      showToast({
        type: "error",
        message: t(
          "auth.adminRequired",
          "No autorizado: se requieren permisos de administrador"
        ),
      });
    }
  }, [shouldRedirectAdmin, showToast, t]);

  useEffect(() => {
    if (shouldRedirectTasks && !notifiedRef.current.tasks) {
      notifiedRef.current.tasks = true;
      showToast({
        type: "error",
        message: t(
          "auth.createTasksRequired",
          "No autorizado: se requieren permisos para crear tareas"
        ),
      });
    }
  }, [shouldRedirectTasks, showToast, t]);

  // Limpiar flags de onboarding cuando tiene familias o completó el onboarding
  const isOnboardingDone =
    hasFamilies ||
    Boolean(activeFamily?.id) ||
    availableFamilies.length > 0 ||
    checkUserHasFamily(userDoc);

  useEffect(() => {
    if (isOnboardingDone) {
      try {
        sessionStorage.removeItem('redirecting_to_onboarding');
        sessionStorage.removeItem('completing_onboarding');
      } catch {}
    }
  }, [isOnboardingDone]);

  // Mostrar loader mientras autenticación, datos o permisos están cargando
  if (loading || !onboardingCheckComplete || permissionsLoading || activeFamilyLoading || !initialLoadComplete) {
    // Mensaje contextual según estado
    let loaderMessage = t("auth.verifyingPermissions", "Verificando permisos...");

    if (!onboardingCheckComplete && user) {
      loaderMessage = t("auth.checkingSetup", "Verificando configuración...");
    } else if (isFromOnboarding) {
      loaderMessage = t("onboarding.finalizing", "Finalizando configuración...");
    } else if (!initialLoadComplete) {
      loaderMessage = t("auth.restoringSession", "Restaurando sesión...");
    }

    return <GlobalLoader message={loaderMessage} />;
  }

  if (!user) {
    return (
      <Navigate to="/signin" replace state={{ from: location }} />
    );
  }

  // Redirigir al onboarding solo si el usuario no tiene familias y no completó onboarding
  const isRedirectingToOnboarding = (() => {
    try {
      return sessionStorage.getItem('redirecting_to_onboarding') === 'true';
    } catch {
      return false;
    }
  })();

  const isSkippedOnboarding = (() => {
    try {
      return localStorage.getItem('onboarding_skipped') === '1';
    } catch {
      return false;
    }
  })();

  const needsOnboardingRedirect = !isOnboardingDone && (isRedirectingToOnboarding || onboardingCheckComplete) && !isSkippedOnboarding;

  if (needsOnboardingRedirect) {
    return <Navigate to="/onboarding/welcome" replace />;
  }

  if (shouldRedirectMember) {
    return <Navigate to="/groups/home-groups" replace />;
  }

  if (shouldRedirectFamilyMode) {
    return <Navigate to="/groups/home-groups" replace />;
  }

  if (shouldRedirectAdmin) {
    return <Navigate to="/groups/home-groups" replace />;
  }

  if (shouldRedirectTasks) {
    return <Navigate to="/groups/home-groups" replace />;
  }

  return <>{children}</>;
};

export default RequireAuth;
